import React, { useState } from 'react';
import { NavLink as RouterNavLink } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Tooltip,
  Container,
  Typography,
  Stack,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import { useUserContext } from '@/context_providers/user_context/UserProvider';
import NavbarLogo from './logo/NavbarLogo';
import { useHandleScreenSize } from '@/context_providers/screen_context/ScreenSizeProvider';
import { Routes } from '@/enums/Routes';
import SwitchViewBanner from '../banners/dev_banners/SwitchViewBanner';
import COLORS from '@/resources/style/Themes';
import { ArrowDropDown } from '@mui/icons-material';

export const SafeguardNavBar = () => {
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [anchorElAppSupport, setAnchorElAppSupport] = useState(null);
  const [anchorElCompassMgmt, setAnchorElCompassMgmt] = useState(null);
  const { isAuthenticated, user, logout } = useAuth0();
  const { isSafeguardAdmin, isSafeguardDev, isSafeguardManufacturingUser } =
    useUserContext();
  const { screenType } = useHandleScreenSize();

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleOpenAppSupportMenu = (event) => {
    setAnchorElAppSupport(event.currentTarget);
  };

  const handleCloseAppSupportMenu = () => {
    setAnchorElAppSupport(null);
  };

  const handleOpenCompassMgmtMenu = (event) => {
    setAnchorElCompassMgmt(event.currentTarget);
  };

  const handleCloseCompassMgmtMenu = () => {
    setAnchorElCompassMgmt(null);
  };

  const logoutWithRedirect = () =>
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });

  return (
    <AppBar
      position="fixed"
      sx={{ backgroundColor: COLORS.SAFEGUARD_BLUE, height: '65px' }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          {/* Logo for Desktop */}
          <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
            <NavbarLogo screenType={screenType} />
          </Box>

          {/* Mobile Menu Button */}
          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="large"
              aria-label="open drawer"
              onClick={handleOpenNavMenu}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{ display: { xs: 'block', md: 'none' } }}
            >
              {(isSafeguardAdmin || isSafeguardDev) && (
                <MenuItem
                  component={RouterNavLink}
                  to={Routes.HOME}
                  onClick={handleCloseNavMenu}
                >
                  Manage Organizations
                </MenuItem>
              )}
              {(isSafeguardAdmin || isSafeguardDev) && (
                <MenuItem
                  component={RouterNavLink}
                  to={Routes.COMPASS_CORE}
                  onClick={handleCloseNavMenu}
                >
                  Compass Core
                </MenuItem>
              )}
              {isSafeguardDev && (
                <Stack>
                  <MenuItem
                    component={RouterNavLink}
                    to={Routes.APP_INBOX}
                    onClick={handleCloseNavMenu}
                  >
                    App Inbox
                  </MenuItem>
                  <MenuItem
                    component={RouterNavLink}
                    to={Routes.APP_VERSIONING}
                    onClick={handleCloseNavMenu}
                  >
                    App Versioning
                  </MenuItem>
                  <MenuItem
                    component={RouterNavLink}
                    to={Routes.SUPPORT_LINKS}
                    onClick={handleCloseNavMenu}
                  >
                    Support Links
                  </MenuItem>
                  <MenuItem
                    component={RouterNavLink}
                    to={Routes.DFU_FILE_MANAGEMENT}
                    onClick={handleCloseNavMenu}
                  >
                    DFU File Management
                  </MenuItem>
                  <MenuItem
                    component={RouterNavLink}
                    to={Routes.PROTOTYPE_FIRMWARE}
                    onClick={handleCloseNavMenu}
                  >
                    Prototype Firmware
                  </MenuItem>
                  <MenuItem
                    component={RouterNavLink}
                    to={Routes.DEVICE_FIRMWARE}
                    onClick={handleCloseNavMenu}
                  >
                    Device Firmware Versioning
                  </MenuItem>
                  <MenuItem
                    component={RouterNavLink}
                    to={Routes.COMPASS_ERROR_LOG}
                    onClick={handleCloseNavMenu}
                  >
                    COMPASS Error Logs
                  </MenuItem>
                  <MenuItem
                    component={RouterNavLink}
                    to={Routes.ANALYTICS}
                    onClick={handleCloseNavMenu}
                  >
                    Analytics
                  </MenuItem>
                </Stack>
              )}
              {(isSafeguardDev || isSafeguardManufacturingUser) && (
                <MenuItem
                  component={RouterNavLink}
                  to={Routes.MANUFACTURING}
                  onClick={handleCloseNavMenu}
                >
                  Manufacturing
                </MenuItem>
              )}
            </Menu>
          </Box>

          {/* Logo for Mobile */}
          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
            <NavbarLogo screenType={screenType} />
          </Box>

          {/* Desktop Menu Items */}
          <Box
            sx={{
              flexGrow: 1,
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
            }}
          >
            {(isSafeguardAdmin || isSafeguardDev) && (
              <Button
                component={RouterNavLink}
                to={Routes.HOME}
                sx={{ my: 2, color: 'white', display: 'block' }}
              >
                Manage Organizations
              </Button>
            )}
            {(isSafeguardAdmin || isSafeguardDev) && (
              <Button
                component={RouterNavLink}
                to={Routes.COMPASS_CORE}
                sx={{ my: 2, color: 'white', display: 'block' }}
              >
                Compass Core
              </Button>
            )}
            {isSafeguardDev && (
              <>
                <Button
                  onClick={handleOpenAppSupportMenu}
                  sx={{
                    my: 2,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  endIcon={<ArrowDropDown />}
                >
                  App Support
                </Button>
                <Menu
                  id="app-support-menu"
                  anchorEl={anchorElAppSupport}
                  open={Boolean(anchorElAppSupport)}
                  onClose={handleCloseAppSupportMenu}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                >
                  <MenuItem
                    component={RouterNavLink}
                    to={Routes.APP_INBOX}
                    onClick={handleCloseAppSupportMenu}
                  >
                    App Inbox
                  </MenuItem>
                  <MenuItem
                    component={RouterNavLink}
                    to={Routes.APP_VERSIONING}
                    onClick={handleCloseAppSupportMenu}
                  >
                    App Versioning
                  </MenuItem>
                  <MenuItem
                    component={RouterNavLink}
                    to={Routes.SUPPORT_LINKS}
                    onClick={handleCloseAppSupportMenu}
                  >
                    Support Links
                  </MenuItem>
                </Menu>

                <Button
                  onClick={handleOpenCompassMgmtMenu}
                  sx={{
                    my: 2,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  endIcon={<ArrowDropDown />}
                >
                  Compass Management
                </Button>
                <Menu
                  id="compass-management-menu"
                  anchorEl={anchorElCompassMgmt}
                  open={Boolean(anchorElCompassMgmt)}
                  onClose={handleCloseCompassMgmtMenu}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                >
                  <MenuItem
                    component={RouterNavLink}
                    to={Routes.DFU_FILE_MANAGEMENT}
                    onClick={handleCloseCompassMgmtMenu}
                  >
                    DFU File Management
                  </MenuItem>
                  <MenuItem
                    component={RouterNavLink}
                    to={Routes.PROTOTYPE_FIRMWARE}
                    onClick={handleCloseCompassMgmtMenu}
                  >
                    Prototype Firmware
                  </MenuItem>
                  <MenuItem
                    component={RouterNavLink}
                    to={Routes.DEVICE_FIRMWARE}
                    onClick={handleCloseCompassMgmtMenu}
                  >
                    Device Firmware Versioning
                  </MenuItem>
                  <MenuItem
                    component={RouterNavLink}
                    to={Routes.COMPASS_ERROR_LOG}
                    onClick={handleCloseCompassMgmtMenu}
                  >
                    COMPASS Error Logs
                  </MenuItem>
                  <MenuItem
                    component={RouterNavLink}
                    to={Routes.ANALYTICS}
                    onClick={handleCloseCompassMgmtMenu}
                  >
                    Analytics
                  </MenuItem>
                </Menu>
              </>
            )}
            {(isSafeguardDev || isSafeguardManufacturingUser) && (
              <Button
                component={RouterNavLink}
                to={Routes.MANUFACTURING}
                sx={{ my: 2, color: 'white', display: 'block' }}
              >
                Manufacturing
              </Button>
            )}
          </Box>

          {/* User Menu */}
          <Box sx={{ flexGrow: 0, display: 'flex', alignItems: 'center' }}>
            <Box
              onClick={handleOpenUserMenu}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography>{user.name}</Typography>
              <Tooltip title="Open settings">
                <IconButton sx={{ p: 0 }}>
                  <ArrowDropDown style={{ color: COLORS.WHITE }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Menu
              sx={{ mt: '45px' }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              <MenuItem onClick={logoutWithRedirect}>
                <PowerSettingsNewIcon sx={{ mr: 1 }} />
                Log out
              </MenuItem>
            </Menu>
            {(isSafeguardDev ||
              isSafeguardManufacturingUser ||
              isSafeguardAdmin) && <SwitchViewBanner />}
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default SafeguardNavBar;
